/** @prettier */

import {memo, useEffect, useState} from 'react';
import {Card, CardContent, Grid, Box, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';

//MTH component import
import BarChartGraph from '../../StackBarChart';
import LinearWithValueLabel from '../../LinearProgressWithLabel';
import {StaticDatePicker, StaticComparisonDatePicker} from './DatePicker';
import PolygonalLineGraph from '../../PolygonalLineGraph';
import TextFieldSelectBox from '../../SelectBox';
import {PowerTrendSelectButton} from './molculate/PowerTrendButton';
import {AddButton} from '../../BasicButton';
//interface import
import * as apiIF from '../../../Type/api';
//API import
import {getSettingUser} from '../../../API/API_Collection';
//type import
import {GraphFormType, GraphDataRange, graphProps} from '../../../Type/TypeDef';
//Style import
import '../../../css/common/common.css';

import {useGetStartEndDate} from '../../../hooks/graph/useGetStartEndDate';
import {useModbusOUChangeStartEndDate} from '../../../hooks/graph/useModbusChangeStartEndDate';
import {makeModbusOUGraphData} from '../../../functions/makeModbusGraphData';
import {iconBlack, pullDownMenu} from '../../../define';
import {makeXLabel} from '../../../functions/graphFunction';

/**
 * Modbus室外機グラフ処理
 */
const ModbusOUGraph = memo((props: {graphProps: graphProps}) => {
  const trans = useTranslation().t; //翻訳関数
  //prettier-ignore
  const {siteID, graphTitle, graphTarget, graphTargetID, graphType, setGraphParam} = props.graphProps;

  //日付の表示（1日/1週間/1か月/1年/10年）
  const [dateRange, setDateRange] = useState<GraphDataRange>('day');
  //prettier-ignore
  const [barGraphData, setBarGraphData] = useState<ApexAxisChartSeries['0']['data'] >([]); //棒グラフデータ
  //prettier-ignore
  const [comparisonBarGraphData, setComparisonBarGraphData] = useState<ApexAxisChartSeries['0']['data']>([]); //棒グラフデータ（比較日）
  //prettier-ignore
  const [lineGraph, setLineGraph] = useState<{name: string; data: ApexAxisChartSeries['0']['data']}[]>([]); //折れ線グラフデータ
  //prettier-ignore
  const [comparisonLineGraph, setComparisonLineGraph] = useState<{name: string; data: ApexAxisChartSeries['0']['data']}[]>([]); //折れ線グラフデータ（比較日）
  const [xDate, setXDate] = useState<string[] | []>([]); //通常日の日付データ
  const [comparisonXDate, setComparisonXDate] = useState<string[]>([]); //比較日の日付データ
  const [xAxisLabel, setXAxisLabel] = useState<string[]>([]); //x軸に表示するラベル
  //prettier-ignore
  const [comparisonXAxisLabel, setComparisonXAxisLabel] = useState<string[]>([]); //比較グラフのx軸に表示するラベル
  //prettier-ignore
  const [yTitle, setYTitle] = useState(`${trans('ModbusGraph.line.yAxisTitle.pressure.mpa')}`); //Y軸タイトル（折れ線グラフ用）
  const [graphLegend, setGraphLegend] = useState<string[]>([]); //グラフに表示する凡例
  const [activeComparison, setActiveComparison] = useState(false); //比較データ表示判定

  //prettier-ignore
  //グラフに表示する日付の範囲を取得
  const {timeZone, graphStartDate, graphEndDate, comparisonGraphStartDate, comparisonGraphEndDate, setGraphDispDate, setComparisonGraphDispDate} = useGetStartEndDate(siteID)

  /** グラフの表示項目 */
  const selectedItem: (keyof apiIF.resParameters.modbusOUStates)[] = [
    'high-pressure',
    'low-pressure',
  ];

  //日付や対象が変更されたとき、グラフに描画するデータを再取得
  const {modbusStateData, csvFileNameDate, graphDispError, apiWait} =
    useModbusOUChangeStartEndDate({
      siteID: siteID,
      graphStartDate: graphStartDate,
      graphEndDate: graphEndDate,
      graphType: graphType,
      timeZone: timeZone,
      dateRange: dateRange,
      selectedItem: selectedItem,
    });
  const {
    modbusStateData: comparisonModbusStateData,
    csvFileNameDate: comparisonCsvFileNameDate,
    graphDispError: comparisonGraphDispError,
    apiWait: comparisonApiWait,
  } = useModbusOUChangeStartEndDate({
    siteID: siteID,
    graphStartDate: comparisonGraphStartDate,
    graphEndDate: comparisonGraphEndDate,
    graphType: graphType,
    timeZone: timeZone,
    dateRange: dateRange,
    selectedItem: selectedItem,
  });

  //受信APIデータ変更時、グラフ表示項目変更時にグラフ描画用データを作成する
  useEffect(() => {
    if (modbusStateData === null) return;

    //prettier-ignore
    /** グラフ表示項目名テーブル */
    const itemNameTable: {
      [item: string]: string; //fiapID: 表示名
    } = {
      'high-pressure' : trans('ModbusGraph.line.graphItem.compressorHighPressure'), //コンプ高圧
      'low-pressure'  : trans('ModbusGraph.line.graphItem.compressorLowPressure'), //コンプ低圧
    };
    const result = makeModbusOUGraphData({
      graphType: graphType,
      dateRange: dateRange,
      graphTargetID: graphTargetID,
      timeZone: timeZone,
      modbusStateData: modbusStateData,
      selectedItem: selectedItem,
      trans: trans,
    });

    //x軸の時間データ
    setXDate(result.xLabel);
    //折れ線グラフデータ
    setLineGraph(
      result.yArrayLine.map(value => {
        if (value.name in itemNameTable) {
          return {name: itemNameTable[value.name], data: value.data};
        } else {
          return value;
        }
      })
    );
    //折れ線グラフの凡例データ
    setGraphLegend(
      result.yArrayLine.map(value => {
        return itemNameTable[value.name];
      })
    );
    //棒グラフデータ
    //現在は消費電力のみ
    if (result.yArrayBar.length > 0) {
      setBarGraphData(result.yArrayBar[0].data);
    }
    //圧力単位からY軸のタイトルを作成
    getSettingUser().then(response => {
      if (response['unit'].pressure === 'MPa') {
        setYTitle(`${trans('ModbusGraph.line.yAxisTitle.pressure.mpa')}`);
      } else {
        setYTitle(`${trans('ModbusGraph.line.yAxisTitle.pressure.bar')}`);
      }
    });
  }, [trans, modbusStateData, graphStartDate, graphTargetID]);

  //比較用データの変更時、比較グラフ描画用データを作成する
  useEffect(() => {
    if (modbusStateData === null) return;

    //prettier-ignore
    /** グラフ表示項目名テーブル */
    const itemNameTableComparison: {
      [item: string]: string; //fiapID: 表示名
    } = {
      'high-pressure' : trans('ModbusGraph.line.graphItem.comparisonCompressorHighPressure'), //コンプ高圧（比較日）
      'low-pressure'  : trans('ModbusGraph.line.graphItem.comparisonCompressorLowPressure'), //コンプ低圧（比較日）
    };
    const comparisonResult = makeModbusOUGraphData({
      graphType: graphType,
      dateRange: dateRange,
      graphTargetID: graphTargetID,
      timeZone: timeZone,
      modbusStateData: comparisonModbusStateData,
      selectedItem: selectedItem,
      trans: trans,
    });

    //x軸の時間データ
    setComparisonXDate(comparisonResult.xLabel);
    //折れ線グラフデータ
    setComparisonLineGraph(
      comparisonResult.yArrayLine.map(value => {
        if (value.name in itemNameTableComparison) {
          return {name: itemNameTableComparison[value.name], data: value.data};
        } else {
          return value;
        }
      })
    );
    //棒グラフデータ
    //現在は消費電力のみ
    if (comparisonResult.yArrayBar.length > 0) {
      setComparisonBarGraphData(comparisonResult.yArrayBar[0].data);
    }
  }, [
    trans,
    comparisonModbusStateData,
    comparisonGraphStartDate,
    graphTargetID,
  ]);

  //x軸に表示するラベルを作成する
  useEffect(() => {
    makeXLabel({
      xDate: xDate,
      comparisonXDate: comparisonXDate,
      dateRange: dateRange,
      graphType: graphType,
      activeComparison: activeComparison,
      setXAxisLabel: setXAxisLabel,
      setComparisonXAxisLabel: setComparisonXAxisLabel,
    });
  }, [xDate, comparisonXDate, activeComparison]);

  //表示範囲が1ヵ月間、または1年間の状態で折れ線グラフへ移動した場合、グラフ表示範囲を1日に変更
  useEffect(() => {
    if (
      graphType === 'line' &&
      (dateRange === 'month' || dateRange === 'year')
    ) {
      setDateRange('day');
    }
  }, [graphType]);

  /** 比較機能のON/OFFを切り替える関数 */
  const handleOnComparison = () => {
    setActiveComparison(!activeComparison);
  };

  /**
   * グラフ形式を決定
   * @param type グラフ形式
   */
  const selectPowerTrend = (type: GraphFormType) => {
    setGraphParam(graphTarget, graphTargetID, type);
  };

  /**
   * グラフ
   * @returns JSX.Element
   */
  const GraphDisplay = () => {
    if (graphType === 'line') {
      //折れ線グラフの場合
      if (xAxisLabel.length === 0) return <></>;
      if (lineGraph.length === 0) return <></>;

      /* グラフ表示用データ */
      const totalLineGraph = [...lineGraph];
      //日付取得範囲が１日かつ比較状態の場合、グラフ表示用データに比較データを追加
      if (dateRange === 'day' && activeComparison) {
        totalLineGraph.push(...comparisonLineGraph);
      }
      return (
        <PolygonalLineGraph
          xLabel={xAxisLabel}
          comparisonXLabel={comparisonXAxisLabel}
          yTitle={yTitle}
          csvFileNameDate={csvFileNameDate}
          csvFileNameTarget={graphTitle}
          graph={totalLineGraph}
          comparisonGraph={comparisonLineGraph}
          graphLegend={graphLegend}
          graphUnits={['pressureUnit', 'pressureUnit']}
          activeComparison={activeComparison}
          graphTarget={graphTarget}
          dateRange={dateRange}
        />
      );
    } else if (graphType === 'bar') {
      //棒グラフの場合
      if (barGraphData.length === 0) return <></>;
      if (activeComparison) {
        return (
          <BarChartGraph
            graphMode="Aggregation"
            graphTarget={graphTarget}
            xLabel={xAxisLabel}
            dateRange={dateRange}
            csvFileNameDate={`${csvFileNameDate},${comparisonCsvFileNameDate}`}
            csvFileNameTarget={graphTitle}
            barGraphData1={barGraphData}
            comparisonBarGraphData1={comparisonBarGraphData}
          />
        );
      }
      return (
        <BarChartGraph
          graphMode="Aggregation"
          graphTarget={graphTarget}
          xLabel={xAxisLabel}
          dateRange={dateRange}
          csvFileNameDate={csvFileNameDate}
          csvFileNameTarget={graphTitle}
          barGraphData1={barGraphData}
        />
      );
    }
    return <></>;
  };

  return (
    <>
      <Card>
        <CardContent className="graph__card__main--otherUnit">
          {/* 消費電力とトレンドグラフの選択 */}
          <PowerTrendSelectButton
            graphType={graphType}
            selectPowerTrend={selectPowerTrend}
          />
          {/* グラフデータ設定欄 */}
          {timeZone !== '' && (
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              className="graph__card__main__menu">
              <Grid item xs="auto" container alignItems="center" spacing={2}>
                {/* カレンダー */}
                <Grid item xs="auto">
                  <StaticDatePicker
                    setGraphDisplayDate={setGraphDispDate}
                    timeZone={timeZone}
                    dateRange={dateRange}
                  />
                </Grid>
                {/* 比較用カレンダー */}
                <Grid item xs>
                  {activeComparison ? (
                    <StaticComparisonDatePicker
                      setGraphDisplayDate={setComparisonGraphDispDate}
                      timeZone={timeZone}
                      dateRange={dateRange}
                      onCloseClick={handleOnComparison}
                    />
                  ) : (
                    //比較用カレンダー表示ボタン
                    <AddButton
                      onClick={handleOnComparison}
                      buttonText={trans('ModbusGraph.buttonTxt.comparison')}
                    />
                  )}
                </Grid>
              </Grid>
              <Grid item xs="auto">
                {/* グラフ表示期間設定セレクトボックス */}
                {graphType === 'line' ? (
                  <TextFieldSelectBox
                    label={trans('ModbusGraph.txtField.label.graphRange')}
                    choices={pullDownMenu.LINE_GRAPH_RANGE}
                    value={dateRange}
                    setValue={setDateRange}
                    id={'graphRangeSelect'}
                    class="graph__card__main__menu__selectBox"
                  />
                ) : (
                  <TextFieldSelectBox
                    label={trans('ModbusGraph.txtField.label.graphRange')}
                    choices={pullDownMenu.GRAPH_RANGE}
                    value={dateRange}
                    setValue={setDateRange}
                    id={'graphRangeSelect'}
                    class="graph__card__main__menu__selectBox"
                  />
                )}
              </Grid>
            </Grid>
          )}
          {/* グラフ */}
          {apiWait || comparisonApiWait ? (
            <Stack justifyContent="center" alignItems="center">
              {graphDispError || comparisonGraphDispError ? (
                <h1 className="graph__card__main__errorMessage">
                  {trans('ModbusGraph.errMsg.gettingDataFailure')}
                </h1>
              ) : (
                <Box className="graph__card__main__progress">
                  <LinearWithValueLabel />
                </Box>
              )}
            </Stack>
          ) : graphDispError || comparisonGraphDispError ? (
            <Stack justifyContent="center" alignItems="center">
              <h1 className="graph__card__main__errorMessage">
                {trans('ModbusGraph.errMsg.gettingDataFailure')}
              </h1>
            </Stack>
          ) : (
            <GraphDisplay />
          )}
        </CardContent>
      </Card>
    </>
  );
});

export {ModbusOUGraph};
